import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Check, ArrowRight, Loader2 } from "lucide-react";

const FAQ = [
  ["Can I change plans later?", "Yes. Owners and administrators can upgrade or downgrade anytime from the portal, and any difference is prorated automatically."],
  ["Does RideStamp replace my broker or MART system?", "No. RideStamp works alongside your existing broker and MART systems — it never replaces them, and your trip data and operational history always stay yours."],
  ["What happens to my data if I cancel?", "You keep full access until the end of your billing period, and your operational history remains yours to export at any time."],
  ["Do you offer a demo?", "Absolutely. Request a demo and we'll walk you through RideStamp with your own workflow and answer any questions."],
  ["Is there a setup fee or contract?", "No setup fees and no long-term contract — choose monthly or annual billing and change whenever you need."],
  ["Is there a free trial?", "Yes. Every plan — Starter, Growth and Fleet — starts with a 2-month free trial. You won't be charged until the trial ends, and you can cancel anytime before then."],
  ["What counts toward my plan limit?", "Plans are sized by fleet: every vehicle in your fleet counts toward the limit (retired vehicles don't). Drivers are unlimited on every plan. As your fleet grows past a tier, upgrading unlocks more vehicles instantly."],
];

export default function PricingPage() {
  usePageMeta("pricing");
  const navigate = useNavigate();
  const { status, user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [note, setNote] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [sub, setSub] = useState(null);

  const canBuy = status === "authed" && ["owner", "administrator", "admin"].includes(user?.role);

  useEffect(() => {
    api.get("/billing/plans")
      .then((r) => { setPlans(r.data.plans); setNote(r.data.note); })
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (!canBuy) { setSub(null); return; }
    api.get("/billing/subscription").then((r) => setSub(r.data.subscription)).catch(() => setSub(null));
  }, [canBuy]);

  const subscribed = sub && ["active", "past_due"].includes(sub.status);

  const annual = cycle === "annual";

  const startCheckout = async (plan, trial = false) => {
    const lk = (annual && plan.lookup_key_annual) ? plan.lookup_key_annual : plan.lookup_key;
    setBusy(lk);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lk, origin_url: window.location.origin, trial });
      window.location.href = data.checkout_url;
    } catch (e) {
      setBusy(null);
      if (e.response?.status === 401) navigate("/login");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white" data-testid="pricing-page">
      <div className="relative overflow-hidden bg-slate-950 pt-32 pb-20 text-white lg:pt-40 lg:pb-24">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #3b82f6 0, transparent 42%)" }} />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
            Plans &amp; Pricing
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">Scale with your fleet</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-slate-300 lg:text-lg">
            {note || "Priced per company size. Every plan keeps your operational history yours — RideStamp works alongside your broker systems, never replacing them."}
          </p>

          <div className="mt-8 inline-flex items-center rounded-full border border-slate-700 bg-slate-900/60 p-1" data-testid="billing-cycle-toggle">
            <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!annual ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>
              Monthly
            </button>
            <button onClick={() => setCycle("annual")} data-testid="cycle-annual"
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${annual ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>
              Annual
              <span className="rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">2 months free</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        {plans === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <div key={plan.id} data-testid={`pricing-card-${plan.id}`}
                className={`relative flex flex-col rounded-2xl border p-7 transition-all hover:-translate-y-1 hover:shadow-xl ${plan.popular ? "border-blue-600 shadow-lg shadow-blue-100" : "border-slate-200"}`}>
                {plan.popular && (
                  <span className="absolute -top-3 left-7 rounded-full bg-blue-700 px-3 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" data-testid="pricing-popular-badge">
                    Most Popular
                  </span>
                )}
                <div className="font-display text-xl font-bold text-slate-900">{plan.name}</div>
                <p className="mt-1 text-sm text-slate-500">{plan.tagline}</p>
                <div className="mt-5 min-h-[86px]">
                  {plan.custom ? (
                    <div className="font-display text-4xl font-bold text-slate-900">Custom</div>
                  ) : annual ? (
                    <>
                      <div className="flex items-end gap-1">
                        <span className="font-display text-4xl font-bold text-slate-900">${(plan.price_annual ?? plan.price * 10).toLocaleString()}</span>
                        <span className="mb-1.5 text-sm text-slate-500">/yr</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">${Math.round((plan.price_annual ?? plan.price * 10) / 12)}/mo billed annually · 2 months free vs ${(plan.price * 12).toLocaleString()} monthly</div>
                    </>
                  ) : (
                    <div className="flex items-end gap-1">
                      <span className="font-display text-4xl font-bold text-slate-900">${plan.price}</span>
                      <span className="mb-1.5 text-sm text-slate-500">/mo</span>
                    </div>
                  )}
                  <div className="mt-1 text-sm font-semibold text-blue-700">{plan.vehicles}</div>
                  {plan.trial && <div className="mt-1 text-xs font-semibold text-emerald-600" data-testid={`pricing-trial-note-${plan.id}`}>2-month free trial</div>}
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => plan.custom ? navigate("/request-demo") : subscribed ? navigate("/portal/billing") : (canBuy ? startCheckout(plan) : navigate("/request-demo"))}
                  disabled={busy === (annual && plan.lookup_key_annual ? plan.lookup_key_annual : plan.lookup_key)}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`mt-7 w-full gap-1.5 rounded-full font-semibold ${plan.popular ? "bg-blue-700 text-white hover:bg-blue-800" : plan.custom ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-blue-600 text-white hover:bg-blue-500"}`}>
                  {plan.custom ? "Contact sales" : subscribed ? (sub.plan_id === plan.id ? "Manage your plan" : "Switch in billing") : canBuy ? (annual ? "Start annual plan" : "Subscribe monthly") : "Request a demo"} <ArrowRight className="h-4 w-4" />
                </Button>
                {plan.trial && canBuy && !subscribed && (
                  <Button variant="outline" onClick={() => startCheckout(plan, true)} disabled={!!busy} data-testid={`pricing-trial-${plan.id}`}
                    className="mt-2 w-full rounded-full border-emerald-600/40 font-semibold text-emerald-700 hover:bg-emerald-50">
                    Start 2-month free trial
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="mt-10 text-center text-sm text-slate-500">
          Already a RideStamp customer?{" "}
          <button onClick={() => navigate("/login")} data-testid="pricing-signin-link" className="font-semibold text-blue-700 hover:underline">
            Sign in to manage your plan
          </button>
        </p>

        <div className="mx-auto mt-20 max-w-3xl" data-testid="pricing-faq">
          <h2 className="text-center font-display text-3xl font-bold tracking-tight text-slate-900">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold text-slate-900">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
